/**
 * Published studies (plant-analytics B-12c, brief 4.7 "Digest and study pages"): typed wrappers over the member study
 * read routes and their hooks. Backend `internal/twin/study_routes.go`; the JSON there is the contract.
 *
 *   GET /v1/twin/studies?limit=&offset=     the org's published studies, newest first
 *   GET /v1/twin/studies/{study_id}         one study: its metadata, the document and its claims
 *   GET /v1/twin/studies/{study_id}/html    the stored page; ?inline=1 is the print route
 *
 * WHO SEES WHAT is the server's, the digests' rule: a platform admin sees every study of the active org, internal ones
 * included; anyone else only the customer-visible ones, and an internal or withdrawn study is 404 to them. The web never
 * re-derives it: an "Internal" badge shows only because the data says `visibility: "internal"`.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { statusOf } from "./error";
import { useDigestsLive, type DigestClaim, type DigestPlantClock } from "./digests";
import type { RecommendationStates } from "./recommendations";

/** One published study in the list (twin.StudySummary). `id` is its snapshot's id: /app/twin/studies/{id}. */
export interface StudySummary {
  id: string;
  kind: "monthly_study" | "adhoc_study";
  /** The period's first plant day, YYYY-MM-DD. */
  period_from: string;
  /** The plant day after its last (half-open), YYYY-MM-DD. */
  period_to: string;
  status: "published" | "withdrawn";
  visibility: "customer" | "internal";
  title: string;
  published_at: string;
  report_run_id: string | null;
  plant_clock: DigestPlantClock;
}

/** GET /v1/twin/studies (httpx.List of StudySummary). `total` is the length of this page. */
export interface StudyList {
  items: StudySummary[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /v1/twin/studies/{study_id} (twin.StudyDetail). */
export interface StudyDetail extends StudySummary {
  engine_version: string;
  contract_version: string;
  /** sha256 of the document as published, hex. */
  content_hash: string;
  registry: Record<string, unknown>;
  /** The reportdoc/v1 document as published. */
  document: Record<string, unknown>;
  /** The claims side panel's claims, read as the stored page reads them; empty when they could not be read. */
  claims?: DigestClaim[];
  /** Each claim action's latest event, by its recommendation key (L1). */
  recommendation_states?: RecommendationStates;
}

/** The list reads at most this many studies at once (the server's cap). */
export const STUDIES_PAGE_LIMIT = 200;

const s = (id: string) => `/v1/twin/studies/${encodeURIComponent(id)}`;

export const studiesApi = {
  list: () => api.get<StudyList>(`/v1/twin/studies?limit=${STUDIES_PAGE_LIMIT}`),
  /** Whether the caller has any study to read: one asked for, for the nav. */
  any: () => api.get<StudyList>("/v1/twin/studies?limit=1"),
  get: (id: string) => api.get<StudyDetail>(s(id)),
};

/**
 * The study's page as HTML text, through the app's authenticated client, from the print route (`?inline=1`). The page
 * is self-contained and inert (no script, no URL, its own CSP meta); the view shows it in a sandboxed iframe's srcDoc
 * and never as the app's own markup.
 */
export async function fetchStudyPage(id: string): Promise<string> {
  const blob = await api.getBlob(`${s(id)}/html?inline=1`);
  return blob.text();
}

/** True when the study routes can be asked at all: the digests' gate (a live API, a signed-in user, an active org). */
export const useStudiesLive = useDigestsLive;

export const STUDIES_KEY = ["twin-studies"] as const;
export const STUDIES_NAV_KEY = ["twin-studies-nav"] as const;
export const STUDY_KEY = ["twin-study"] as const;
export const STUDY_PAGE_KEY = ["twin-study-page"] as const;

/** A 403 and a 404 are answers, never retried. */
const retryUnlessRefused = (count: number, err: unknown) => {
  const st = statusOf(err);
  return st !== 403 && st !== 404 && count < 2;
};

/** The org's studies as the caller may see them, newest first. */
export function useStudiesQuery(enabled = true) {
  const live = useStudiesLive();
  return useQuery({
    queryKey: STUDIES_KEY,
    queryFn: studiesApi.list,
    enabled: live && enabled,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/** Whether the caller has any study, asked for with one, for the nav; `enabled` is the nav item's own gate. */
export function useStudiesNavQuery(enabled: boolean) {
  const live = useStudiesLive();
  return useQuery({
    queryKey: STUDIES_NAV_KEY,
    queryFn: studiesApi.any,
    enabled: live && enabled,
    staleTime: 5 * 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * The nav's "Studies" item, pure: shown to a platform admin always, and to anyone else once the list holds a study
 * they may read. It fails closed: loading, a refusal and a failure all hide it from a member.
 */
export function studyNavVisible(isPlatformAdmin: boolean, page: Pick<StudyList, "items"> | undefined): boolean {
  return isPlatformAdmin || (page?.items.length ?? 0) > 0;
}

/** One study with its document and claims. */
export function useStudyQuery(id: string | undefined) {
  const live = useStudiesLive();
  return useQuery({
    queryKey: [...STUDY_KEY, id ?? ""],
    queryFn: () => studiesApi.get(id as string),
    enabled: live && !!id,
    staleTime: 5 * 60_000,
    retry: retryUnlessRefused,
  });
}

/** The study's stored page as HTML text, for the sandboxed iframe. A stored page never changes. */
export function useStudyPageQuery(id: string | undefined) {
  const live = useStudiesLive();
  return useQuery({
    queryKey: [...STUDY_PAGE_KEY, id ?? ""],
    queryFn: () => fetchStudyPage(id as string),
    enabled: live && !!id,
    staleTime: Infinity,
    retry: retryUnlessRefused,
  });
}
